/**
 * Image types feature — entry point
 *
 * Structure:
 *   hooks/useImageTypes.js         ← list, toggle, delete, save
 *   components/ImageTypeModal.jsx  ← add/edit form modal
 */
import { useState } from 'react'
import { RiAddLine, RiDeleteBinLine, RiEditLine } from 'react-icons/ri'
import {
  Btn, Toggle, Badge, PageHeader, EmptyState,
  Modal,
} from '../../components/ui/design.jsx'
import { useImageTypes } from './hooks/useImageTypes.js'
import ImageTypeModal from './components/ImageTypeModal.jsx'

export default function ImageTypesPage() {
  const { types, loading, toggleActive, remove, save } = useImageTypes()
  const [editing, setEditing] = useState(null)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tipe Gambar"
        description="Slot yang menentukan di mana gambar ditampilkan di website."
        action={
          <Btn onClick={() => setEditing('new')}>
            <RiAddLine /> Tambah Tipe
          </Btn>
        }
      />

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {/* Head */}
        <div
          className="hidden md:grid border-b border-gray-100 bg-gray-50/70 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
          style={{ gridTemplateColumns: '1fr 130px 90px 80px 100px' }}
        >
          <span>Nama</span>
          <span>Maks. aktif</span>
          <span>Terisi</span>
          <span>Slot</span>
          <span className="text-right">Aksi</span>
        </div>

        <div className="divide-y divide-gray-100">
          {loading && <EmptyState message="Memuat…" />}
          {!loading && types.length === 0 && <EmptyState message="Belum ada tipe gambar." hint="Klik 'Tambah Tipe' untuk membuat slot baru." />}
          {types.map((t) => (
            <div
              key={t.id}
              className="flex flex-col gap-3 px-6 py-4 hover:bg-gray-50/50 transition-colors md:grid md:items-center md:gap-4"
              style={{ gridTemplateColumns: '1fr 130px 90px 80px 100px' }}
            >
              <div>
                <div className="text-sm font-semibold text-gray-900">{t.name}</div>
                <div className="mt-0.5 text-xs text-gray-400 font-mono">{t.slug}</div>
                {t.description && <div className="mt-0.5 text-xs text-gray-400">{t.description}</div>}
              </div>
              <div>
                {t.is_unlimited
                  ? <Badge variant="active">Unlimited</Badge>
                  : <span className="text-sm font-medium text-gray-700">{t.max_active}</span>}
              </div>
              <div className="text-sm text-gray-600">
                {t.is_unlimited ? t.active_images_count : `${t.active_images_count}/${t.max_active}`}
              </div>
              <div>
                <Toggle checked={t.is_active} onChange={() => toggleActive(t)} />
              </div>
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => setEditing(t)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors" title="Edit">
                  <RiEditLine className="text-base" />
                </button>
                <button onClick={() => remove(t)} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Hapus">
                  <RiDeleteBinLine className="text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <ImageTypeModal
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (form) => { await save(form); setEditing(null) }}
        />
      )}
    </div>
  )
}
